import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface CoverLetterRow {
  id: string;
  user_id: string;
  url: string;
  resume_key: string;
  pdf_key: string;
  text: string;
  model: string;
  created_at: number;
}

// List rows: full text replaced with text_chars + jobs/companies JOIN.
export interface CoverLetterListRow extends Omit<CoverLetterRow, "text"> {
  text_chars: number;
  job_title: string | null;
  company_id: string | null;
  company_name: string | null;
}

export interface GenerateCoverLetterInput {
  url: string;
  resume_id: string;
  force?: boolean;
}

export interface CoverLetterResponse {
  cover_letter: CoverLetterRow;
  cached: boolean;
}

export interface CoverLetterError {
  status: number;
  error: string;
  retry_after_seconds?: number;
  detail?: string;
  stage?: string;
}

export function useCoverLetters() {
  return useQuery({
    queryKey: ["cover-letters"],
    queryFn: () =>
      api<{ cover_letters: CoverLetterListRow[] }>("/api/cover-letter"),
  });
}

export function useCoverLetter(id: string | null) {
  return useQuery({
    queryKey: ["cover-letter", id],
    enabled: id !== null,
    queryFn: () =>
      api<{ cover_letter: CoverLetterRow }>(`/api/cover-letter/${id}`),
  });
}

export function useDeleteCoverLetter() {
  const qc = useQueryClient();
  return useMutationToast<{ ok: true }, CoverLetterError, string>({
    mutationFn: async (id) => {
      try {
        return await api(`/api/cover-letter/${id}`, { method: "DELETE" });
      } catch (err) {
        throw toCoverLetterError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cover-letters"] });
    },
    success: "Cover letter deleted.",
    errorPrefix: "Delete failed",
  });
}

export function useGenerateCoverLetter() {
  const qc = useQueryClient();
  return useMutationToast<
    CoverLetterResponse,
    CoverLetterError,
    GenerateCoverLetterInput
  >({
    mutationFn: async (input) => {
      try {
        return await api<CoverLetterResponse>("/api/cover-letter", {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toCoverLetterError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cover-letters"] });
    },
    success: (data) =>
      data.cached ? "Loaded cached cover letter." : "Cover letter generated.",
    errorPrefix: "Cover letter failed",
  });
}

// PDF endpoint — used as the href on a "Download PDF" link. Browser will
// stream the bytes back inline; the API sets content-disposition.
export function pdfHref(id: string): string {
  return `/api/cover-letter/${id}/pdf`;
}

function toCoverLetterError(err: unknown): CoverLetterError {
  if (err instanceof ApiError) {
    let payload: {
      error?: string;
      retry_after_seconds?: number;
      detail?: string;
      stage?: string;
    } = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error: payload.error ?? err.message ?? "request_failed",
      retry_after_seconds: payload.retry_after_seconds,
      detail: payload.detail,
      stage: payload.stage,
    };
  }
  return { status: 0, error: "network_error" };
}
